import classNames from "classnames"

import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import { getTransparentBorderColorClass } from "@utils/V2/color"
import React, { ReactNode } from "react"

type Props = {
  text: string
  description?: string
  icon?: ReactNode
  imageSrc?: string
  onClick?: () => void
  isSelected?: boolean
  showArrow?: boolean
  disabled?: boolean
}

const AnswerButton = ({
  text,
  description,
  icon,
  imageSrc,
  onClick,
  isSelected = false,
  showArrow = true,
  disabled = false,
}: Props) => {
  if (imageSrc) {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={classNames(
          "rounded-xs group flex h-full w-full overflow-hidden bg-white shadow-[0_10px_20px_0_rgba(0,0,0,0.05)] transition-colors duration-200 disabled:pointer-events-none disabled:opacity-50 sm:flex-col",
          isSelected
            ? "border-[2px] border-blue"
            : "border-[2px] hover:border-blue",
          !isSelected && getTransparentBorderColorClass(Color.Charcoal)
        )}
      >
        <div className="w-[120px] flex-shrink-0 sm:w-full">
          <GatsbyStoryblokImage
            image={imageSrc}
            alt={text}
            aspectRatio="4:3"
            imageObjectFit="cover"
            className="h-full w-full"
          />
        </div>
        <div className="flex flex-1 flex-col items-start p-20 text-left sm:flex-none">
          <Typography
            text={text}
            font="grotesk"
            weight="medium"
            size="body-lg"
            color={Color.Black}
          />
          {description && (
            <Typography
              text={description}
              font="grotesk"
              weight="book"
              size="body-sm"
              color={Color.Charcoal}
              className="mt-[2px] opacity-50"
            />
          )}
        </div>
      </button>
    )
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={classNames(
        "rounded-xs group flex w-full bg-white shadow-[0_10px_20px_0_rgba(0,0,0,0.05)] transition-colors duration-200 disabled:pointer-events-none disabled:opacity-50",
        isSelected
          ? "border-[2px] border-blue p-[19px]"
          : "border-[1px] p-20 hover:border-[2px] hover:border-blue hover:p-[19px]",
        !isSelected && getTransparentBorderColorClass(Color.Charcoal)
      )}
    >
      {icon && (
        <div className="mr-12 flex h-[32px] w-[32px] flex-shrink-0 items-center justify-center self-center">
          {icon}
        </div>
      )}
      <div className="flex flex-col items-start text-left">
        <Typography
          text={text}
          font="grotesk"
          weight="medium"
          size="body-lg"
          color={Color.Black}
        />

        {description && (
          <Typography
            text={description}
            font="grotesk"
            weight="book"
            size="body-sm"
            color={Color.Charcoal}
            className="mt-[2px] opacity-50"
          />
        )}
      </div>

      {showArrow && <ArrowIcon className="mb-auto ml-auto mt-auto" />}
    </button>
  )
}

export default AnswerButton
